import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@rentflowdev/ui'
import { DEFAULT_LOCALE } from '@/types'
import type { RentalHistoryEntry } from '@/types'
import { useListRentalHistoryQuery } from '@/api'

// Backend never populates `actionLabel` — translate the raw `action` enum
// client-side instead of leaving history rows with no description.
const ACTION_KEYS: Record<RentalHistoryEntry['action'], string> = {
	CREATED:                'rent.history.actions.created',
	UPDATED:                'rent.history.actions.updated',
	STATUS_CHANGED:         'rent.history.actions.statusChanged',
	NOTE_ADDED:             'rent.history.actions.noteAdded',
	TASK_CREATED:           'rent.history.actions.taskCreated',
	DOCUMENT_CREATED:       'rent.history.actions.documentCreated',
	INVOICE_CREATED:        'rent.history.actions.invoiceCreated',
	PAYMENT_RECEIVED:       'rent.history.actions.paymentReceived',
	TENANT_CONTACT_CHANGED: 'rent.history.actions.tenantContactChanged',
}

function formatTimestamp(iso: string): string {
	const date = new Date(iso)
	const today = new Date()
	const time = date.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	if (date.toDateString() === today.toDateString()) return `Сегодня, ${time}`
	return `${date.toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'short' })}, ${time}`
}

interface RentalHistorySectionProps {
	rentalId: string
}

export function RentalHistorySection({ rentalId }: RentalHistorySectionProps) {
	const { t } = useTranslation()
	const { data } = useListRentalHistoryQuery({ rentalId })
	const entries: RentalHistoryEntry[] = data?.entries ?? []

	if (entries.length === 0) {
		return (
			<p className="text-sm text-(--color-muted-foreground)">{t('rent.history.empty')}</p>
		)
	}

	return (
		<div className="flex flex-col">
			{entries.map((entry) => (
				<div
					key={entry.id}
					className="flex items-center gap-3 border-b border-(--color-border) px-4 py-3 last:border-b-0"
				>
					{entry.actorName && (
						<Avatar className="size-5 shrink-0 rounded-full">
							{entry.actorAvatar && <AvatarImage src={entry.actorAvatar} alt={entry.actorName} />}
							<AvatarFallback className="text-xs">{entry.actorName.charAt(0)}</AvatarFallback>
						</Avatar>
					)}
					<p className="flex-1 text-sm text-(--color-foreground)">
						{entry.actorName && <span className="font-[500]">{entry.actorName}</span>}
						{' '}
						{t(ACTION_KEYS[entry.action])}
					</p>
					<span className="shrink-0 text-xs text-(--color-muted-foreground)">
						{formatTimestamp(entry.createdAt)}
					</span>
				</div>
			))}
		</div>
	)
}
